import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useAccess } from '@/lib/access';
import { useCycle } from '@/lib/cycle';
import { Loading, Empty } from '@/components/UI';
import { GlassCard, KpiCard, SectionStatusCard, StatusBadge, OutlineButton, PageHeader } from '@/components/design';
import { formatDate, formatDateTime } from '@/lib/format';
import { AlertTriangle, CalendarCheck2, MessageSquareWarning, ShieldCheck, ClipboardCheck, History, Briefcase, BadgeCheck, Factory, Wrench, Truck, Users, FlaskConical, Leaf, GitBranch, CloudUpload, ArrowRight, UserCheck } from 'lucide-react';
import HrSnapshot from '@/components/HrSnapshot';
import QualitySnapshot, { useQualityMrm, qualityCardStatus } from '@/components/QualitySnapshot';

// `sections`: section-master keys that make the card visible (none = everyone).
const SECTIONS = [
  { key: 'previous_actions', label: 'Previous Actions', tone: 'purple', icon: History },
  { key: 'business_sales', label: 'Business & Sales', tone: 'orange', icon: Briefcase, sections: ['business_sales'] },
  { key: 'quality', label: 'Quality', tone: 'blue', icon: BadgeCheck, sections: ['quality'] },
  { key: 'production', label: 'Production (JSON)', tone: 'cyan', icon: Factory, sections: ['production'] },
  { key: 'maintenance', label: 'Maintenance (JSON)', tone: 'blue', icon: Wrench, sections: ['maintenance'] },
  { key: 'supply_chain', label: 'Supply Chain', tone: 'orange', icon: Truck, sections: ['supply_chain'] },
  { key: 'hr_safety', label: 'People & Safety', tone: 'indigo', icon: Users, sections: ['hr_safety', 'hr'] },
  { key: 'npd', label: 'NPD (JSON)', tone: 'green', icon: FlaskConical, sections: ['npd'] },
  { key: 'hr', label: 'HR (JSON)', tone: 'purple', icon: UserCheck, sections: ['hr'] },
  { key: 'environment', label: 'Environment', tone: 'green', icon: Leaf, sections: ['environment'] },
  { key: 'change_points', label: '4M Change Points', tone: 'purple', icon: GitBranch, sections: ['quality'] },
];

const LINKS = { previous_actions:'/actions', business_sales:'/business-sales', quality:'/quality', production:'/production', maintenance:'/maintenance', supply_chain:'/supply-chain', hr_safety:'/hr-safety', npd:'/npd', hr:'/hr-safety', environment:'/environment', change_points:'/change-points' };

const HISTORY_LEN = 6; // cycles (including current) used for KPI sparklines

export default function Dashboard() {
  const { currentCycle, currentUnit, cycles } = useCycle();
  const { user } = useAuth();
  const { can } = useAccess();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [prod, setProd] = useState(null);
  const [maint, setMaint] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [actions, setActions] = useState([]);
  const [quality, setQuality] = useState(null);
  const [activeKpi, setActiveKpi] = useState(null);
  const [history, setHistory] = useState({ adherence: [], complaints: [], pm: [], openActions: [] });
  // Quality card state comes from the normalized Quality object (Operations quality entries +
  // manual form), not from the manual-form flag alone — same (year, month, unit) as the other sections.
  const qualityData = useQualityMrm(currentCycle?.id);
  const { quality: qualityMrm, error: qualityErr, loading: qualityLoading } = qualityData;

  useEffect(() => {
    if (!currentCycle || !currentUnit) { setLoading(false); return; }
    setLoading(true);
    const y = currentCycle.year, m = currentCycle.month, u = currentUnit.code;
    Promise.all([
      api.get(`/data/production/${y}/${m}?unit=${u}`).catch(() => ({ data: { has_data: false } })),
      api.get(`/data/machine-maintenance/${y}/${m}?unit=${u}`).catch(() => ({ data: { has_data: false } })),
      api.get(`/data/alerts/${y}/${m}?unit=${u}`).catch(() => ({ data: { data: [] } })),
      api.get(`/actions?cycle_id=${currentCycle.id}`).catch(() => ({ data: [] })),
      api.get(`/entries/${currentCycle.id}/quality`).catch(() => ({ data: null })),
    ]).then(([p, m2, a, ac, q]) => {
      setProd(p.data?.data || null);
      setMaint(m2.data?.data || null);
      setAlerts((a.data?.data || []).slice(0, 10));
      setActions(ac.data || []);
      setQuality(q.data?.data || null);
    }).finally(() => setLoading(false));
  }, [currentCycle, currentUnit]);

  // Sparkline history: the previous cycles of this unit, oldest → newest,
  // ending with the current cycle. Fetched after the main data so it never
  // blocks the dashboard.
  useEffect(() => {
    if (!currentCycle || !currentUnit) { setHistory({ adherence: [], complaints: [], pm: [], openActions: [] }); return; }
    let cancelled = false;
    const u = currentUnit.code;
    const ordered = (cycles || [])
      .filter((c) => c.unit_id === currentUnit.id && (c.year < currentCycle.year || (c.year === currentCycle.year && c.month <= currentCycle.month)))
      .sort((a, b) => (a.year - b.year) || (a.month - b.month))
      .slice(-HISTORY_LEN);
    if (!ordered.some((c) => c.id === currentCycle.id)) ordered.push(currentCycle);
    Promise.all(ordered.map(async (c) => {
      const [p, mm, ac, q] = await Promise.all([
        api.get(`/data/production/${c.year}/${c.month}?unit=${u}`).catch(() => null),
        api.get(`/data/machine-maintenance/${c.year}/${c.month}?unit=${u}`).catch(() => null),
        api.get(`/actions?cycle_id=${c.id}`).catch(() => null),
        api.get(`/entries/${c.id}/quality`).catch(() => null),
      ]);
      const pd = p?.data?.data, md = mm?.data?.data, acts = ac?.data, qd = q?.data?.data;
      return {
        adherence: pd ? Number(pd.overall_adherence_pct || 0) : null,
        pm: md ? Number(md.pm_compliance?.overall_pct || 0) : null,
        openActions: Array.isArray(acts) ? acts.filter((x) => ['open', 'in_progress', 'overdue'].includes(x.status)).length : null,
        complaints: qd ? Number(qd.summary?.total_complaints ?? 0) : null,
      };
    })).then((rows) => {
      if (cancelled) return;
      const pick = (k) => rows.map((r) => r[k]).filter((v) => v !== null && v !== undefined);
      setHistory({ adherence: pick('adherence'), complaints: pick('complaints'), pm: pick('pm'), openActions: pick('openActions') });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [currentCycle, currentUnit, cycles]);

  if (!currentUnit) return <Empty title="No unit accessible" hint="Contact admin to assign a unit to your account." />;
  if (!currentCycle) return <Empty title={`No cycle for ${currentUnit.name}`} hint="Create a new cycle from the top-right to begin." action={user.role === 'admin' ? <div className="text-sm text-slate-500">Click "New Cycle" in the header.</div> : null} />;
  if (loading) return <Loading />;

  const openActions = actions.filter((a) => ['open', 'in_progress'].includes(a.status)).length;
  const overdueActions = actions.filter((a) => a.status === 'overdue').length;
  const closedActions = actions.filter((a) => a.status === 'closed').length;
  const ss = currentCycle.section_status || {};

  const toggleKpi = (key) => setActiveKpi((k) => (k === key ? null : key));

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Dashboard — ${currentCycle.label}`}
        subtitle={<span className="inline-flex items-center gap-2 flex-wrap"><span>Meeting Date: {formatDate(currentCycle.meeting_date)}</span><span className="text-slate-300">•</span><span>Deadline: {formatDate(currentCycle.data_entry_deadline)}</span></span>}
        actions={<StatusBadge status={currentCycle.status} label={currentCycle.status?.toUpperCase()} size="md" />}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5" data-testid="kpi-grid">
        {can('production') && (
        <KpiCard
          tone="green"
          icon={CalendarCheck2}
          label="Schedule Adherence"
          value={prod ? `${prod.overall_adherence_pct || 0}%` : 'No data'}
          target="98%"
          spark={history.adherence}
          active={activeKpi === 'adherence'}
          onClick={() => toggleKpi('adherence')}
          testId="kpi-schedule-adherence"
        />
        )}
        {can('quality') && (
        <KpiCard
          tone="blue"
          icon={MessageSquareWarning}
          label="Customer Complaints"
          value={quality?.summary?.total_complaints ?? '0'}
          target="0"
          spark={history.complaints}
          active={activeKpi === 'complaints'}
          onClick={() => toggleKpi('complaints')}
          testId="kpi-customer-complaints"
        />
        )}
        {can('maintenance') && (
        <KpiCard
          tone="orange"
          icon={ShieldCheck}
          label="PM Compliance"
          value={maint ? `${maint.pm_compliance?.overall_pct || 0}%` : 'No data'}
          target="95%"
          spark={history.pm}
          active={activeKpi === 'pm'}
          onClick={() => toggleKpi('pm')}
          testId="kpi-pm-compliance"
        />
        )}
        <KpiCard
          tone="red"
          icon={ClipboardCheck}
          label="Open Actions"
          value={`${openActions + overdueActions}`}
          target={`${closedActions} closed`}
          spark={history.openActions}
          active={activeKpi === 'actions'}
          onClick={() => toggleKpi('actions')}
          testId="kpi-open-actions"
        />
      </div>

      <GlassCard title="Section Status" action={<OutlineButton icon={CloudUpload} onClick={() => nav('/uploads')} data-testid="btn-open-upload-center">Open Upload Center</OutlineButton>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-3 sm:gap-4">
          {SECTIONS.filter((s) => !s.sections || can(...s.sections)).map((s) => {
            let status = ss[s.key] || 'pending';
            const isJson = ['production', 'maintenance', 'npd', 'hr'].includes(s.key);
            let label = isJson ? (status === 'uploaded' ? 'Uploaded' : 'Awaiting Upload') : (status === 'submitted' ? 'Submitted' : status === 'approved' ? 'Approved' : 'Pending');
            if (s.key === 'quality' && status !== 'approved') {
              const qc = qualityCardStatus(qualityMrm, qualityErr, qualityLoading);
              status = qc.status; label = qc.label;
            }
            return (
              <SectionStatusCard
                key={s.key}
                label={s.label}
                tone={s.tone}
                icon={s.icon}
                status={status}
                statusLabel={label}
                onClick={() => nav(LINKS[s.key])}
                testId={`section-card-${s.key}`}
              />
            );
          })}
        </div>
      </GlassCard>

      {can('quality') && <QualitySnapshot cycleId={currentCycle.id} data={qualityData} onOpen={() => nav('/quality')} />}

      {can('hr', 'hr_safety') && <HrSnapshot cycleId={currentCycle.id} onOpen={() => nav('/hr-safety')} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
        <GlassCard title="Latest Alerts" action={prod ? <span className="text-xs text-slate-500">Production JSON</span> : null}>
          {alerts.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center">No alerts — upload production JSON to see alerts.</div> : (
            <div className="space-y-2">
              {alerts.map((a, i) => {
                const critical = a.severity === 'critical';
                return (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl border" style={{ background: critical ? '#FFF1F2' : '#FFF7E8', borderColor: critical ? '#FECDD3' : '#FDE68A' }}>
                    <AlertTriangle size={15} className="mt-0.5 shrink-0" style={{ color: critical ? '#EF4444' : '#F59E0B' }} />
                    <div className="flex-1 text-sm min-w-0">
                      <div className="font-medium text-[var(--aampl-text)]">{a.message}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{a.alert_type} • {formatDateTime(a.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </GlassCard>
        <GlassCard title="Action Items Summary" action={<button className="btn-ghost text-sm" onClick={() => nav('/actions')}>View All <ArrowRight size={14} /></button>}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat value={actions.length} label="Total" tone="indigo" />
            <Stat value={openActions} label="Open" tone="orange" />
            <Stat value={overdueActions} label="Overdue" tone="red" />
            <Stat value={closedActions} label="Closed" tone="green" />
          </div>
          {actions.length > 0 && (
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {actions.slice(0, 5).map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 text-sm p-2.5 rounded-xl border border-[var(--aampl-border)] bg-white/70">
                  <div className="flex-1 truncate"><span className="font-mono text-xs text-slate-500 mr-2">{a.action_number}</span>{a.description}</div>
                  <StatusBadge status={a.status} label={a.status} />
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

const STAT_TONES = {
  indigo: { bg: 'linear-gradient(135deg, #FFFFFF, #EEF3FF)', text: '#3730A3', ring: '#C7D2FE' },
  orange: { bg: 'linear-gradient(135deg, #FFFFFF, #FFF7E8)', text: '#C2410C', ring: '#FDE68A' },
  red: { bg: 'linear-gradient(135deg, #FFFFFF, #FFF1F2)', text: '#DC2626', ring: '#FECDD3' },
  green: { bg: 'linear-gradient(135deg, #FFFFFF, #ECFDF5)', text: '#15803D', ring: '#BBF7D0' },
};

function Stat({ value, label, tone }) {
  const t = STAT_TONES[tone];
  return (
    <div className="text-center p-3 rounded-xl border" style={{ background: t.bg, borderColor: t.ring }}>
      <div className="text-2xl font-extrabold tracking-tight" style={{ color: t.text }}>{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider mt-0.5" style={{ color: t.text, opacity: 0.8 }}>{label}</div>
    </div>
  );
}
